import {Box, Stack} from '@mui/material';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import {useEffectFn} from '@ngneat/effects-hooks';
import {emitOnce} from '@ngneat/elf';
import {useObservable} from '@ngneat/react-rxjs';
import {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {Client, clientsQuery, clientsService, searchClientsEffect,} from '@store/clients';
import {clientsUnitsService} from '@store/clients/units';

import SearchComponent from '@components/inputs/search/Search.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import NewTableComponent from '@components/table/NewTable.component';

import {clientsServicesService} from '@store/clients/services';
import {documentsService} from '@store/documents';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {TaxRule} from '@store/common/organisation.enums';

const ClientsListTable = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const searchClients = useEffectFn(searchClientsEffect);

  const [{clients, loading}] = useObservable(clientsQuery.clients$);
  const [selectedClients] = useObservable(clientsQuery.selectedClients$);

  const [filterStatus] = useObservable(clientsQuery.status$);
  const [filters] = useObservable(clientsQuery.filters$);
  const [sortModel] = useObservable(clientsQuery.sort$);
  const [tablePagination] = useObservable(clientsQuery.pagination$);

  useEffect(() => {
    searchClients({status: filterStatus, filters, tablePagination, sort: sortModel});
  }, [filters, filterStatus, searchClients, sortModel, tablePagination]);

  return (
    <Stack flex={1} spacing={2} pb="25px">
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box width="500px">
          <SearchComponent
            value={filters.search}
            handleChange={(value) => {
              emitOnce(() => {
                clientsService.setFilters('search')(value);
                clientsService.setPagination('page')(1);
              });
            }}/>
        </Box>
        <TableFiltersComponent
          filters={filters}
          handleSave={clientsService.setFilters('tableFilters')}
          handleReset={() => clientsService.resetFilters()}/>
      </Stack>
      <NewTableComponent
        loading={loading}
        handleReload={() => searchClients({status: filterStatus, filters, tablePagination, sort: sortModel})}
        columns={[
          COLUMN_TYPES.TEXT_BOLD({
            field: 'organisation.id',
            label: t('clients.table.id'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Client>) => params.row.id,
            width: 100
          }),
          COLUMN_TYPES.TEXT_BOLD({
            field: 'organisation.name',
            label: t('clients.table.name'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Client>) => params.row.name,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT_BOLD({
            field: 'organisation.inTaxRule',
            label: t('clients.table.taxRule'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<Client, Client>) => TaxRule.selectItems().find(i => i.value === params.row.taxRule)?.label,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT({
            field: 'address.country',
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, Client>) => params.row.country,
            color: 'secondary', label: t('clients.table.country'), width: 150
          }),
          COLUMN_TYPES.ACTIONS({
            handleShowRow: (client: Client) => {
              clientsUnitsService.resetStore();
              clientsServicesService.resetStore();
              documentsService.resetStore();
              navigate(`../${client.id}`);
            },
          }),
        ]}
        handlePageSizeChange={clientsService.setPagination('limit')}
        pageSize={tablePagination.limit}
        handlePageChange={(page) => clientsService.setPagination('page')(page + 1)}
        page={tablePagination.page - 1}
        handleSortModelChange={clientsService.setSort}
        sortModel={[sortModel]}
        handleRowSelected={clientsService.setSelectedClients}
        rowsSelected={selectedClients}
        totalRows={clientsQuery.totalRows}
        data={clients}/>
    </Stack>
  );
};

export default ClientsListTable;
